import React from 'react';
import { Receipt, AlertCircle, CreditCard, BarChart3, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { useFacturationConfig } from './useFacturationConfig';

export default function BillingDashboardPage() {
  const { t } = useTranslation();
  useFacturationConfig();
  const p = (key, opts) => t(`modules.facturation.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/facturation/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم الفوترة' })}
      subtitle={p('subtitle', { defaultValue: 'متابعة الفواتير والتحصيل' })}
      icon={Receipt}
      accent="from-pink-600 to-rose-800"
      recentTitle={p('recent_invoices', { defaultValue: 'آخر الفواتير' })}
      quickLinks={[
        { label: p('link_create', { defaultValue: 'إنشاء مستند' }), href: '/document-creation', icon: Receipt },
        { label: p('link_invoices', { defaultValue: 'الفواتير' }), href: '/facturation/factures', icon: Receipt },
        { label: p('link_payments', { defaultValue: 'المدفوعات' }), href: '/payments', icon: CreditCard },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/facturation/analyses-rapports', icon: BarChart3 },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, ['paid', 'complete', 'settled']);

        return {
          hero: {
            label: p('kpi_total_invoiced', { defaultValue: 'إجمالي الفوترة' }),
            value: formatBrandMoney(kpis.totalInvoiced || 0),
            icon: Receipt,
            sub: `${p('kpi_collection_rate', { defaultValue: 'نسبة التحصيل' })}: ${kpis.collectionRate ?? 0}%`,
          },
          kpis: withKpiVariants([
            { label: p('kpi_outstanding', { defaultValue: 'المبالغ المستحقة' }), value: formatBrandMoney(kpis.outstanding || 0), icon: CreditCard },
            { label: p('kpi_overdue', { defaultValue: 'فواتير متأخرة' }), value: kpis.overdue ?? 0, icon: AlertCircle },
            { label: p('kpi_collection_rate', { defaultValue: 'نسبة التحصيل %' }), value: `${kpis.collectionRate ?? 0}%`, icon: BarChart3 },
            {
              label: p('kpi_invoices_count', { defaultValue: 'عدد الفواتير' }),
              value: sumRowValues(chartRows),
              icon: FileText,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_payment_status', { defaultValue: 'حالة المدفوعات' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'مؤشرات التحصيل والاستحقاق' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_overdue', { defaultValue: 'فواتير متأخرة' }), value: kpis.overdue ?? 0 },
                { label: p('kpi_collection_rate', { defaultValue: 'نسبة التحصيل %' }), value: kpis.collectionRate ?? 0 },
                { label: p('chart_ranked_title', { defaultValue: 'حالات الدفع' }), value: chartRows.reduce((s, r) => s + (Number(r.value) || 0), 0) },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الفواتير' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'مدفوعة مقابل مستحقة' }),
              active: active || Math.round((kpis.collectionRate ?? 0) * 10),
              inactive: inactive || (kpis.overdue ?? 0),
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات الدفع' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب حالة السداد' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
